import React, { useEffect, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { authApi, authSlice } from '@app/store/services/auth';
import styled from '@2pulse/responsive';
import { KeyboardAvoidingView } from 'react-native';
import { TextInput } from '@app/components';
import { Button, Form } from '@app/components';
import { StyledForms } from '@app/components/styled/form/Form';
import { useToast } from '@app/hooks';
import { extractApiGenericError } from '@app/helpers';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { RootStackParamList } from '@app/types';
import { useAppDispatch } from '@app/store/hooks';

const ResetPasswordView = styled.View({
  width: '100%',
  height: '100%',
  flex: 1,
  alignItems: 'center',
  flexDirection: 'column',
  justifyContent: 'center',
  backgroundColor: '#02182B',
});

const ResetPasswordForm = styled.View({
  width: 420,
});

const ResetPasswordSubmit = styled.View({
  width: 300,
  marginLeft: 60,
});

const Logo = styled.Image({
  marginTop: 21,
  marginBottom: 34,
  alignSelf: 'center',
  height: 70,
  width: 345,
});

const TextError = styled.Text({
  color: 'red',
  marginBottom: 10,
});

const Title = styled.Text({
  color: 'white',
  fontSize: 24,
  fontWeight: 'bold',
  textAlign: 'center',
  marginBottom: 10,
});

const Subtitle = styled.Text({
  color: 'white',
  fontSize: 14,
  textAlign: 'center',
  marginBottom: 20,
});

const schema = yup
  .object({
    password: yup
      .string()
      .min(6, 'Le mot de passe doit contenir au moins 6 caract\u00e8res')
      .required('Ce champ est requis'),
    passwordConfirmation: yup
      .string()
      .oneOf([yup.ref('password')], 'Les mots de passe ne correspondent pas')
      .required('Ce champ est requis'),
  })
  .required();

export const ResetPassword = () => {
  const toast = useToast();
  const dispatch = useAppDispatch();
  const navigation = useNavigation();
  const route = useRoute<RouteProp<RootStackParamList, 'ResetPassword'>>();
  const token = route.params?.token;

  const [success, setSuccess] = useState(false);

  const formMethods = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      password: '',
      passwordConfirmation: '',
    },
  });

  useEffect(() => {
    dispatch(authSlice.actions.logout());
  }, [dispatch]);

  const { errors } = formMethods.formState;

  const [renewPassword, { isLoading }] = authApi.useRenewPasswordMutation();

  const onSubmit = async (data: { password: string; passwordConfirmation: string }) => {
    try {
      await renewPassword({
        token,
        password: data.password,
        passwordConfirmation: data.passwordConfirmation,
      }).unwrap();
      setSuccess(true);
    } catch (error: any) {
      const errorMessage = extractApiGenericError(error) ?? 'Une erreur est survenue.';
      toast.error(errorMessage);
    }
  };

  const handleGoToLogin = () => {
    navigation.navigate('Login');
  };

  if (!token) {
    return (
      <ResetPasswordView>
        <KeyboardAvoidingView behavior="position">
          <Logo source={require('@app/assets/img/logo.png')} />

          <Title>Lien invalide</Title>
          <Subtitle>Le lien de r&eacute;initialisation est invalide ou a expir&eacute;.</Subtitle>

          <ResetPasswordSubmit style={{ marginLeft: 60 }}>
            <Button large title="Retour &agrave; la connexion" onPress={handleGoToLogin} />
          </ResetPasswordSubmit>
        </KeyboardAvoidingView>
      </ResetPasswordView>
    );
  }

  if (success) {
    return (
      <ResetPasswordView>
        <KeyboardAvoidingView behavior="position">
          <Logo source={require('@app/assets/img/logo.png')} />

          <Title>Mot de passe modifi&eacute;</Title>
          <Subtitle>
            Votre mot de passe a &eacute;t&eacute; modifi&eacute; avec succ&egrave;s. Vous pouvez
            maintenant vous connecter.
          </Subtitle>

          <ResetPasswordSubmit style={{ marginLeft: 60 }}>
            <Button large title="Se connecter" onPress={handleGoToLogin} />
          </ResetPasswordSubmit>
        </KeyboardAvoidingView>
      </ResetPasswordView>
    );
  }

  return (
    <ResetPasswordView>
      <KeyboardAvoidingView behavior="position">
        <Logo source={require('@app/assets/img/logo.png')} />

        <Title>Nouveau mot de passe</Title>
        <Subtitle>Entrez votre nouveau mot de passe.</Subtitle>

        {errors.password && <TextError>{errors.password.message}</TextError>}

        <ResetPasswordForm>
          <Form formMethods={formMethods}>
            <StyledForms.Form>
              <TextInput
                name="password"
                label="Mot de passe"
                placeholderTextColor="black"
                secureTextEntry={true}
                autoCapitalize="none"
                autoComplete="off"
              />

              {errors.passwordConfirmation && (
                <TextError>{errors.passwordConfirmation.message}</TextError>
              )}

              <TextInput
                name="passwordConfirmation"
                label="Confirmer le mot de passe"
                placeholderTextColor="black"
                secureTextEntry={true}
                autoCapitalize="none"
                autoComplete="off"
                onSubmitEditing={formMethods.handleSubmit(onSubmit)}
              />

              <ResetPasswordSubmit>
                <Button
                  large
                  title="Modifier le mot de passe"
                  loading={isLoading}
                  onPress={formMethods.handleSubmit(onSubmit)}
                />
              </ResetPasswordSubmit>
            </StyledForms.Form>
          </Form>
        </ResetPasswordForm>
      </KeyboardAvoidingView>
    </ResetPasswordView>
  );
};
